import React, {useRef} from 'react';
import clsx from 'clsx';
import {nanoid} from 'nanoid';
import type {SetOptional} from 'type-fest';
import {type IconDefinition} from '@netx/core-icons';
import {
	type AlertColor,
	LinearProgress,
	Dialog as MuiDialog,
	DialogActions as MuiDialogActions,
	type DialogActionsProps as MuiDialogActionsProps,
	dialogClasses as muiDialogClasses,
	DialogContent as MuiDialogContent,
	type DialogContentProps as MuiDialogContentProps,
	type DialogProps as MuiDialogProps,
	Slide,
	type SlideProps,
} from '@mui/material';
import modalViews from '@netx/core-app/singletons/modalViews';
import {bodyViews} from '@netx/core-app/singletons/bodyViews';
import {fsViews} from '@netx/core-app/singletons/fsViews';
import {
	DialogAlert,
	type DialogAlertProps,
} from '@netx/core-components/DialogAlert';
import {
	DialogTitle,
	type DialogTitleProps,
} from '@netx/core-components/DialogTitle';
import useMediaQuery from '@netx/core-components/hooks/useMediaQuery';
import {
	composeClasses,
	styled,
	useTheme,
	type WithClasses,
} from '@netx/core-components/Theme';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {
	DIALOG_CLASS_NAME,
	dialogClasses,
	type DialogClasses,
	getDialogUtilityClass,
} from './dialogClasses';

export type DialogOwnerState = WithClasses<
	DialogClasses,
	{
		fullSize?: boolean;
		fullHeight?: boolean;
		fullScreenWhenMobile?: boolean;
		disableContentPadding?: boolean;
		severity?: AlertColor;
	}
>;

const useUtilityClasses = (ownerState: DialogOwnerState) => {
	const {classes, disableContentPadding, fullSize} = ownerState;
	const slots = {
		root: ['root', fullSize && 'fullSize'],
		content: ['content', disableContentPadding && 'disableContentPadding'],
		actions: ['actions'],
	};
	return composeClasses(slots, getDialogUtilityClass, classes);
};

// TODO: this is a hack until we can find a better way to manage when and when not to show the main app
// upload dropzone
// we already were doing a check for any modals (backbone) being open, so just continuing that behavior for now...
export let dialogsOpenCount = 0;

export const decreaseDialogsOpenCount = () => {
	dialogsOpenCount = Math.max(dialogsOpenCount - 1, 0);
};
export const increaseDialogsOpenCount = () => {
	dialogsOpenCount++;
};
export const resetDialogsOpenCount = () => {
	dialogsOpenCount = 0;
};
export const getDialogsOpenCount = () => dialogsOpenCount;

const DialogRoot = styled(MuiDialog, {
	name: DIALOG_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => {
	const {fullSize, fullHeight} = ownerState as DialogOwnerState;
	const classes = {
		...(fullSize && {
			[`& .${muiDialogClasses.paper}:not(.${muiDialogClasses.paperFullScreen})`]:
				{
					height: 'calc(100% - 64px)',
					maxWidth: 'initial',
					width: 'calc(100% - 64px)',
				},
		}),
		...(fullHeight && {
			[`& .${muiDialogClasses.paper}:not(.${muiDialogClasses.paperFullScreen})`]:
				{
					height: 'calc(100% - 64px)',
					maxHeight: 'initial',
				},
		}),
	};
	return classes;
});

const DialogActions = styled(MuiDialogActions, {
	name: DIALOG_CLASS_NAME,
	slot: 'Actions',
})(({theme}) => ({
	padding: theme.spacing(2),
}));

const DialogContent = styled(MuiDialogContent, {
	name: DIALOG_CLASS_NAME,
	slot: 'Content',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',
	[`&.${dialogClasses.disableContentPadding}`]: {
		padding: 0,
	},
}));

const DialogProgress = styled(LinearProgress, {
	name: DIALOG_CLASS_NAME,
	slot: 'Progress',
})(({theme}) => ({
	...theme.mixins.absoluteFill,
	bottom: 'initial',
}));

const Transition = React.forwardRef<HTMLElement, SlideProps>(
	function Transition(props, ref) {
		return <Slide direction="up" ref={ref} {...props} />;
	},
);

export interface DialogProps extends WithClasses<
	DialogClasses,
	Omit<MuiDialogProps, 'title'>
> {
	closeDisabled?: boolean;
	// TODO: why missing?
	'data-testid'?: string;
	dialogActions?: React.ReactNode;
	disableContentPadding?: boolean;
	disabled?: boolean;
	error?: React.ReactNode;
	fullSize?: boolean;
	fullHeight?: boolean;
	fullScreenWhenMobile?: boolean;
	isWorking?: boolean;
	severity?: AlertColor;
	title?: React.ReactNode;
	secondaryTitle?: React.ReactNode;
	loading?: boolean;
	showSecondaryTitleSkeletonWhileLoading?: boolean;
	icon?: IconDefinition;
	controls?: React.ReactNode;
	preventBackdropClose?: boolean;
	progress?: number | null;
	DialogActionsProps?: Partial<MuiDialogActionsProps>;
	DialogAlertProps?: Partial<DialogAlertProps>;
	DialogContentProps?: Partial<MuiDialogContentProps>;
	DialogTitleProps?: Partial<DialogTitleProps>;
}

export type WithDialogProps<P = {}> = P &
	Pick<DialogProps, 'open' | 'onClose'> & {
		DialogProps?: SetOptional<Omit<DialogProps, 'open' | 'onClose'>, 'title'>;
	};

/**
 * NetX Dialog
 * Provides a consistent animation/experience
 */
const DialogPrimitive = React.forwardRef<HTMLDivElement, DialogProps>(
	(
		{
			children,
			classes: _classes,
			className,
			closeDisabled,
			controls,
			dialogActions,
			disableContentPadding,
			disabled,
			error,
			fullSize,
			fullHeight,
			fullScreen: _fullScreen,
			fullScreenWhenMobile,
			icon,
			isWorking,
			loading,
			progress,
			preventBackdropClose,
			onClose,
			secondaryTitle,
			showSecondaryTitleSkeletonWhileLoading,
			// TODO: severity styling support for 'danger' / 'info' / 'warning' / 'success'
			severity, // = 'info',
			title,
			DialogActionsProps,
			DialogAlertProps,
			DialogContentProps,
			DialogTitleProps,
			...props
		}: DialogProps,
		ref,
	) => {
		const instanceUuidRef = useRef(nanoid());

		const smallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
		const fullScreen = _fullScreen || (fullScreenWhenMobile && smallScreen);

		const ownerState: DialogOwnerState = {
			classes: _classes,
			className,
			disableContentPadding,
			fullSize,
			fullHeight,
			fullScreenWhenMobile,
			severity,
		};

		const classes = useUtilityClasses(ownerState);

		const handleClose = onClose
			? () => onClose({}, 'escapeKeyDown')
			: undefined;

		const dialogId = `dialog-${instanceUuidRef.current}`;

		const [_disableEnforceFocus, setDisableEnforceFocus] =
			React.useState(false);

		React.useEffect(() => {
			const listener = () => {
				setDisableEnforceFocus(
					modalViews.length > 0 || bodyViews.length > 0 || fsViews.length > 0,
				);
			};
			modalViews.on('change reset add remove', listener);
			bodyViews.on('change reset add remove', listener);
			fsViews.on('change reset add remove', listener);

			return () => {
				modalViews.off('change reset add remove', listener);
				bodyViews.off('change reset add remove', listener);
				fsViews.off('change reset add remove', listener);
			};
		}, []);

		return (
			<DialogRoot
				disableEnforceFocus={_disableEnforceFocus}
				ref={ref}
				aria-labelledby={(title != null && dialogId) || undefined}
				className={clsx(classes.root, className)}
				fullScreen={fullScreen}
				ownerState={ownerState}
				onClose={(event, reason) => {
					if (
						closeDisabled ||
						(preventBackdropClose && reason === 'backdropClick')
					)
						return;
					onClose?.(event, reason);
				}}
				TransitionComponent={Transition}
				{...props}
				TransitionProps={{
					...props.TransitionProps,
					onEnter: splitEventHandler(
						props.TransitionProps?.onEnter,
						// see hack notes above
						() => dialogsOpenCount++,
					),
					onExit: splitEventHandler(
						props.TransitionProps?.onExit,
						// see hack notes above
						// also, never got negative - can't have negative dialogs
						() => (dialogsOpenCount = Math.max(dialogsOpenCount - 1, 0)),
					),
				}}
			>
				{isWorking ? (
					<DialogProgress
						value={progress ?? undefined}
						variant={progress != null ? 'determinate' : 'indeterminate'}
					/>
				) : null}

				{(title && (
					<DialogTitle
						controls={controls}
						disabled={disabled || closeDisabled}
						id={dialogId}
						primary={title}
						secondary={secondaryTitle}
						severity={severity}
						icon={icon}
						onClose={handleClose}
						{...DialogTitleProps}
					/>
				)) ||
					null}

				{error != null && (typeof error !== 'string' || !!error.length) ? (
					<DialogAlert {...DialogAlertProps}>{error}</DialogAlert>
				) : null}

				<DialogContent
					{...DialogContentProps}
					className={clsx(classes.content, DialogContentProps?.className)}
				>
					{children}
				</DialogContent>

				{dialogActions && (
					<DialogActions
						{...DialogActionsProps}
						className={clsx(classes.actions, DialogActionsProps?.className)}
					>
						{dialogActions}
					</DialogActions>
				)}
			</DialogRoot>
		);
	},
);

export const Dialog = DialogPrimitive;

export default Dialog;
